/**
 * Token storage and refresh.
 *
 * Tokens live in IndexedDB rather than localStorage: it survives better on iOS, and
 * the strict CSP (no inline script, no third-party origins) is what actually keeps
 * them safe.
 *
 * Refresh is serialised through a Web Lock. Refresh token ROTATION is enabled, so
 * every refresh invalidates the token it was called with — two tabs (or a tab and the
 * service worker) refreshing concurrently would race, and the loser would be signed
 * out holding a dead token.
 */
import type { AppConfig } from '../config.js';
import { clearAuth, getAuth, setAuth } from '../offline/db.js';
import { refreshTokens, type AuthTokens } from './cognito.js';

const TOKENS_KEY = 'tokens';
const LOCK_NAME = 'rx-token-refresh';

export async function loadTokens(): Promise<AuthTokens | undefined> {
  return getAuth<AuthTokens>(TOKENS_KEY);
}

export async function storeTokens(tokens: AuthTokens): Promise<void> {
  await setAuth(TOKENS_KEY, tokens);
}

export async function signOut(): Promise<void> {
  await clearAuth();
}

/**
 * A valid access token, refreshing if needed.
 *
 * Returns undefined rather than throwing when offline: a failure to refresh must
 * never stop a dose from being written locally. Logging comes first; syncing waits.
 */
export async function currentAccessToken(config: AppConfig): Promise<string | undefined> {
  const tokens = await loadTokens();
  if (!tokens) return undefined;
  if (Date.now() < tokens.expiresAt) return tokens.accessToken;

  const refreshed = await withLock(async () => {
    // Re-read inside the lock: another context may have refreshed while we waited,
    // in which case ours is already stale and theirs is the live one.
    const latest = await loadTokens();
    if (!latest) return undefined;
    if (Date.now() < latest.expiresAt) return latest;

    try {
      const next = await refreshTokens(config, latest.refreshToken);
      await storeTokens(next);
      return next;
    } catch (error) {
      // Only a definitive rejection clears the session. A network failure must leave
      // the refresh token intact so it still works when signal returns.
      if (isDefinitiveAuthFailure(error)) await signOut();
      return undefined;
    }
  });

  return refreshed?.accessToken;
}

function isDefinitiveAuthFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const code = (error as { code?: string }).code ?? '';
  return ['NotAuthorizedException', 'RefreshFailed', 'InvalidTokens', 'UserNotFoundException'].includes(
    code,
  );
}

async function withLock<T>(fn: () => Promise<T>): Promise<T> {
  // lib.dom types navigator.locks as always present, but it is genuinely missing in
  // older iOS WebViews. Degrading to an unlocked refresh is worse only when there are
  // concurrent contexts — and in those browsers there is no lock to be had anyway.
  const locks = (navigator as unknown as { locks?: LockManager }).locks;
  if (!locks) return fn();
  return locks.request(LOCK_NAME, fn) as Promise<T>;
}
